import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TicketsWrapper,
  TicketsContainer,
  PageHeader,
  HeaderLeft,
  TitleRow,
  Title,
  TotalBadge,
  Description,
  ToolbarCard,
  ToolbarRow,
  TableCard,
} from './AdminTickets.styles';
import SearchBar from './components/SearchBar/SearchBar';
import FilterBar, { CloseIcon } from './components/FilterBar/FilterBar';
import { useTicketFilters } from './hooks/useTicketFilters';
import { useTicketsQuery } from './hooks/useTicketsQuery';
import { useClientsQuery } from './hooks/useClientsQuery';
import { useClientUsersQuery } from './hooks/useClientUsersQuery';

import {
  CellSecondaryText,
  MonoText,
  Pill,
  StatusBadge,
  StatusDot,
} from '../../../components/uielements/table/table.styles';
import { Table } from '../../../components/uielements/table';
import {
  ActiveFiltersLabel,
  ActiveFiltersRow,
  FilterTag,
  FilterTagValue,
  RemoveFilterButton,
  ResetAllButton,
} from './components/FilterBar/filterBar.styles';
import {
  EmptyState,
  OutlineButon,
  RetryButton,
  SpinWrapper,
} from '../../../settings/appStyle/global.style';
import { formatDateTime } from '../../../helper/dateRelated';
import { Loader2, RotateCcw } from 'lucide-react';
import TruncatedTitleCell from '../../../components/uielements/TruncatedTitleCell';
import { TicketIdCell, UnviewedCount, UnviewedDot } from '../../UserPages/Tickets/Tickets.styles';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const FILTER_LABELS = {
  status: 'Status',
  type: 'Type',
  startDate: 'From',
  endDate: 'To',
  clientId: 'Client',
  userId: 'User',
};
const STATUS_VALUE_LABELS = { open: 'Open', closed: 'Closed' };

const AdminTickets = () => {
  const navigate = useNavigate();

  const { search, filters, page, setSearch, setFilters, removeFilter, resetFilters, setPage } =
    useTicketFilters();

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    if (searchInput === search) return undefined;
    const timeout = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const queryParams = useMemo(
    () => ({
      search,
      status: filters.status,
      type: filters.type,
      startDate: filters.startDate,
      endDate: filters.endDate,
      clientId: filters.clientId,
      userId: filters.userId,
      page,
      pageSize: PAGE_SIZE,
    }),
    [search, filters, page]
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useTicketsQuery(queryParams);
  const rows = data?.tickets ?? [];
  const total = data?.totalTickets ?? 0;

  // Only used to resolve human-readable names for the active-filter tags.
  // Same query keys as FilterBar's dropdowns, so react-query serves this
  // from cache instead of firing a second network request in the common case.
  const { data: clients = [] } = useClientsQuery();
  const { data: clientUsers = [] } = useClientUsersQuery(filters.clientId);

  const nameLookups = useMemo(
    () => ({
      clientId: new Map(clients.map((c) => [c.id, c.name])),
      userId: new Map(clientUsers.map((u) => [u.id, u.name])),
    }),
    [clients, clientUsers]
  );

  const formatFilterValue = (key, value) => {
    if (key === 'status') return STATUS_VALUE_LABELS[value] || value;
    if (key === 'clientId') return nameLookups.clientId.get(value) || value;
    if (key === 'userId') return nameLookups.userId.get(value) || value;
    return value;
  };

  const activeFilters = useMemo(
    () =>
      Object.entries(filters)
        .filter(([, value]) => value)
        .map(([key, value]) => ({
          key,
          label: FILTER_LABELS[key],
          value: formatFilterValue(key, value),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, nameLookups]
  );

  const handleDashboard = () => navigate('/');
  const handleRowClick = (ticket) => navigate(`/ticket/${ticket._id}`);

  const columns = useMemo(
    () => [
      {
        Header: 'Ticket ID',
        accessor: 'ticketCode',
        width: '20%',
        disableSortBy: true,
        Cell: ({ value, row }) => {
          const unviewed = row?.original?.unviewedQueries ?? 0;
          return (
            <TicketIdCell>
              <MonoText>#{value}</MonoText>
              {unviewed > 0 && (
                <UnviewedDot role="status" aria-label={`${unviewed} unviewed`} title="Unviewed">
                  <UnviewedCount>{unviewed > 9 ? '9+' : unviewed}</UnviewedCount>
                </UnviewedDot>
              )}
            </TicketIdCell>
          );
        },
      },
      {
        Header: 'Title & summary',
        accessor: 'title',
        width: '40%',
        Cell: ({ value, row }) => (
          <div>
            <TruncatedTitleCell text={value} />
            <CellSecondaryText>{row.original.summary}</CellSecondaryText>
          </div>
        ),
      },
      {
        Header: 'Category',
        accessor: 'ticketType',
        width: '10%',
        Cell: ({ value }) => <Pill>{value}</Pill>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: '10%',
        align: 'center',
        Cell: ({ value }) => (
          <StatusBadge $status={value}>
            <StatusDot />
            {value === 'OPEN' ? 'ACTIVE' : 'RESOLVED'}
          </StatusBadge>
        ),
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
        width: '15%',
        Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
      },
    ],
    []
  );

  return (
    <TicketsWrapper>
      <TicketsContainer>
        <PageHeader>
          <HeaderLeft>
            <TitleRow>
              <Title>Your tickets</Title>
              <TotalBadge>{total} Total</TotalBadge>
            </TitleRow>
            <Description>
              Search, filter and manage support requests across all internal queues
            </Description>
          </HeaderLeft>
          <OutlineButon onClick={handleDashboard} type="button">
            Dashboard
          </OutlineButon>
        </PageHeader>

        <ToolbarCard>
          <ToolbarRow>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search tickets by title"
            />
            <FilterBar
              activeFilters={activeFilters}
              appliedFilters={filters}
              onApplyFilters={setFilters}
              onResetFilters={resetFilters}
            />
          </ToolbarRow>

          {activeFilters.length > 0 && (
            <ActiveFiltersRow>
              <ActiveFiltersLabel>Active filters:</ActiveFiltersLabel>
              {activeFilters.map((filter) => (
                <FilterTag key={filter.key}>
                  <span>{filter.label}:</span>
                  <FilterTagValue>{filter.value}</FilterTagValue>
                  <RemoveFilterButton
                    type="button"
                    aria-label={`Remove ${filter.label} filter`}
                    onClick={() => removeFilter(filter.key)}
                  >
                    <CloseIcon />
                  </RemoveFilterButton>
                </FilterTag>
              ))}
              <ResetAllButton type="button" onClick={resetFilters}>
                Reset all
              </ResetAllButton>
            </ActiveFiltersRow>
          )}
        </ToolbarCard>

        <TableCard>
          {isLoading ? (
            <>
              <EmptyState>
                <SpinWrapper>
                  <Loader2 />
                </SpinWrapper>
                Loading your tickets...
              </EmptyState>
            </>
          ) : isError ? (
            <EmptyState role="alert">
              Couldn't load tickets{error?.message ? `: ${error.message}` : ''}. Please try again
              later.
              <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
                <RotateCcw />
                {isFetching ? 'Retrying…' : 'Retry'}
              </RetryButton>
            </EmptyState>
          ) : (
            <Table
              data={rows}
              columns={columns}
              hasFooter
              page={page - 1}
              pageSize={PAGE_SIZE}
              total={total}
              isLoading={isLoading}
              isFetching={isFetching}
              prevPage={() => setPage(Math.max(page - 1, 1))}
              nextPage={() => setPage(page + 1)}
              onRowClick={handleRowClick}
              emptyStateText="No tickets match your search or filters"
            />
          )}
        </TableCard>
      </TicketsContainer>
    </TicketsWrapper>
  );
};

export default AdminTickets;
